import React, { useEffect, useState } from 'react';
import { Copy, Sparkles, ArrowRight } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

/**
 * Featured promo CTA shown on the public WiFi access page after guests
 * connect. Fetches the single admin-chosen featured code from
 * `/api/promo-codes/featured` and renders a copy + book-again card.
 *
 * Designed as a pure stateless presenter — the parent page decides where
 * to mount it, so it can sit between the WiFi credentials and any local
 * recommendations without forcing a layout change.
 */
const FeaturedPromoCard = ({ apiBase, brandUrl = 'https://homesbyli.com' }) => {
  const { toast } = useToast();
  const [promo, setPromo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiBase}/promo-codes/featured`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setPromo(data?.featured || null);
      } catch {
        /* Silent failure — the WiFi page must still work without the CTA. */
      }
    })();
    return () => { cancelled = true; };
  }, [apiBase]);

  if (!promo) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(promo.code);
      toast({ title: 'Code copied!', description: `${promo.code} is on your clipboard` });
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Long-press the code to copy manually',
        variant: 'destructive',
      });
    }
  };

  // Inline-display whatever discount type the admin chose.
  const discountLabel = promo.type === 'percentage'
    ? `${promo.value}% off`
    : `$${promo.value} off`;

  const expiry = promo.end_date ? new Date(promo.end_date) : null;
  const expiryLabel = expiry && !isNaN(expiry.getTime())
    ? expiry.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  // Pre-apply the promo when the guest taps "Book again" so they don't
  // have to retype it on the property page.
  const bookHref = `${brandUrl}/?promo=${encodeURIComponent(promo.code)}`;

  return (
    <div
      className="relative overflow-hidden rounded-2xl border-2 border-rose-200 bg-gradient-to-br from-rose-50 via-orange-50 to-amber-50 p-5 mb-6 shadow-sm"
      data-testid="featured-promo-card"
    >
      <div className="absolute -top-3 -right-3 w-24 h-24 rounded-full bg-rose-200/40 blur-2xl pointer-events-none" />
      <div className="relative flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-rose-600" />
        <p className="text-[11px] font-bold uppercase tracking-wider text-rose-700">
          Loved your stay? Save on the next one
        </p>
      </div>
      <p className="relative text-sm text-gray-700 mb-3">
        Book directly on our website with this code to get <span className="font-semibold text-rose-700">{discountLabel}</span>.
      </p>

      <button
        type="button"
        onClick={handleCopy}
        className="relative w-full flex items-center justify-between gap-3 bg-white border-2 border-dashed border-rose-300 rounded-xl px-4 py-3 mb-3 hover:border-rose-400 hover:bg-rose-50/40 transition group"
        data-testid="featured-promo-copy-btn"
      >
        <span className="font-mono font-bold text-xl tracking-wider text-rose-700">
          {promo.code}
        </span>
        <span className="flex items-center gap-1.5 text-xs text-gray-500 group-hover:text-rose-600 transition">
          <Copy className="w-3.5 h-3.5" />
          Tap to copy
        </span>
      </button>

      <a
        href={bookHref}
        target="_blank"
        rel="noopener noreferrer"
        className="relative flex items-center justify-center gap-2 w-full bg-gradient-to-r from-rose-600 to-orange-600 hover:from-rose-700 hover:to-orange-700 text-white font-semibold rounded-xl px-4 py-3 transition shadow-sm"
        data-testid="featured-promo-book-btn"
      >
        Book your next stay
        <ArrowRight className="w-4 h-4" />
      </a>

      {expiryLabel ? (
        <p className="relative text-[11px] text-gray-500 text-center mt-2">
          Valid until {expiryLabel}
        </p>
      ) : null}
    </div>
  );
};

export default FeaturedPromoCard;
